import React, { useState } from "react";
import styled, { useTheme } from "styled-components";
import { BacktestingScoreBreakdown } from "@shared/types";
import useNewLookRisk, {
  RISK_LEVELS,
} from "features/newMarketplace/NewLook/useNewLookRisk";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

/** Four short bars filled up to the risk level. */
export const RiskBars = ({
  level,
  color,
}: {
  level: number;
  color: string;
}) => (
  <Bars aria-hidden="true">
    {RISK_LEVELS.map((riskLevel, index) => (
      <Bar
        key={riskLevel}
        style={index <= level ? { background: color } : undefined}
      />
    ))}
  </Bars>
);

let gaugeCount = 0;

/** A small semicircle gauge, green to red, with a marker at the level. */
export const RiskGauge = ({ level }: { level: number }) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const [id] = useState(() => `risk-gauge-${gaugeCount++}`);
  const angle = Math.PI * (1 - (level + 0.5) / RISK_LEVELS.length);
  return (
    <svg width={32} height={18} viewBox="0 0 32 18" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor={tokens.up} />
          <stop offset="0.5" stopColor={theme.colors.yellow} />
          <stop offset="1" stopColor={tokens.down} />
        </linearGradient>
      </defs>
      <path
        d="M5 15 A11 11 0 0 1 27 15"
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth={4}
        strokeLinecap="round"
      />
      {level >= 0 && (
        <circle
          cx={16 + 11 * Math.cos(angle)}
          cy={15 - 11 * Math.sin(angle)}
          r={3.2}
          fill={tokens.panel}
          stroke={tokens.ink}
          strokeWidth={1.6}
        />
      )}
    </svg>
  );
};

interface NewLookRiskMeterProps {
  scoreBreakdown: BacktestingScoreBreakdown;
  className?: string;
}

/** The risk level as a quiet meter followed by its name ("Low"). */
const NewLookRiskMeter = ({
  scoreBreakdown,
  className,
}: NewLookRiskMeterProps) => {
  const { level, color, shortLabel, label } = useNewLookRisk(scoreBreakdown);
  return (
    <Wrapper className={className} title={label}>
      <RiskBars level={level} color={color} />
      <Label style={{ color }}>{shortLabel ?? "–"}</Label>
    </Wrapper>
  );
};

export default React.memo(NewLookRiskMeter);

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const Bars = styled.span`
  display: inline-flex;
  gap: 2px;
  flex: none;
`;
const Bar = styled.span`
  width: 8px;
  height: 6px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
`;
const Label = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
